import { useEffect } from "react";
import { blogIndexDescription, blogIndexPath, blogIndexTitle } from "./BlogIndexData";
import type { BlogPost } from "./BlogPostPage";
import { publishedBlogPosts } from "./BlogRegistry";
import { MarketingFooter, MarketingHeader } from "../public/MarketingChrome";

const SITE_URL = "https://meetdoorway.com";

export function BlogIndexPage() {
  const posts = publishedBlogPosts;
  const featuredPost = posts.find((post) => post.featured);
  const remainingPosts = posts.filter((post) => post !== featuredPost);
  useBlogIndexMetadata(posts);

  return (
    <div className="blog-shell blog-index-shell oasis-marketing-shell">
      <MarketingHeader current="blog" />

      <main className="blog-index">
        <header className="blog-index-heading">
          <p className="blog-kicker">{blogIndexTitle}</p>
          <h1>The work after the app works.</h1>
          <p>{blogIndexDescription}</p>
        </header>

        {featuredPost && <FeaturedPost post={featuredPost} />}

        {remainingPosts.length > 0 && (
          <section className="blog-archive" aria-labelledby="all-posts-heading">
            <div className="blog-archive-heading">
              <p className="blog-section-label">Every blog post</p>
              <h2 id="all-posts-heading">Latest posts</h2>
            </div>
            <div className="blog-card-grid">
              {remainingPosts.map((post) => <BlogCard post={post} key={post.path} />)}
            </div>
          </section>
        )}
      </main>

      <MarketingFooter />
    </div>
  );
}

function FeaturedPost({ post }: { post: BlogPost }) {
  return (
    <article className="featured-post">
      <a className="featured-post-image" href={post.path} aria-label={`Read ${post.title}`}>
        <img
          src={post.image}
          alt={post.imageAlt}
          width={post.imageWidth ?? 1440}
          height={post.imageHeight ?? 810}
        />
      </a>
      <div className="featured-post-copy">
        <p className="featured-label"><span aria-hidden="true" />Featured</p>
        <h2><a href={post.path}>{post.title}</a></h2>
        <p>{post.description}</p>
        <PostMeta post={post} />
        <a className="text-link" href={post.path}>Read the blog post <span aria-hidden="true">→</span></a>
      </div>
    </article>
  );
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <article className="blog-card">
      <a className="blog-card-image" href={post.path} tabIndex={-1} aria-hidden="true">
        <img
          src={post.image}
          alt=""
          width={post.imageWidth ?? 1440}
          height={post.imageHeight ?? 810}
        />
      </a>
      <PostMeta post={post} />
      <h3><a href={post.path}>{post.title}</a></h3>
      <p>{post.description}</p>
      <a className="text-link" href={post.path}>Read post <span aria-hidden="true">→</span></a>
    </article>
  );
}

function PostMeta({ post }: { post: BlogPost }) {
  return (
    <p className="post-meta">
      <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
      <span aria-hidden="true" />
      {post.readingTime}
    </p>
  );
}

function useBlogIndexMetadata(posts: BlogPost[]) {
  useEffect(() => {
    const canonicalUrl = new URL(blogIndexPath, SITE_URL).toString();
    const imageUrl = new URL(posts[0]?.image ?? "/assets/doorway-icons/favicon-green.svg", SITE_URL).toString();

    document.title = `${blogIndexTitle} | Doorway`;
    setMeta("name", "description", blogIndexDescription);
    setMeta("name", "robots", "index, follow, max-image-preview:large");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Doorway");
    setMeta("property", "og:title", blogIndexTitle);
    setMeta("property", "og:description", blogIndexDescription);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", imageUrl);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", blogIndexTitle);
    setMeta("name", "twitter:description", blogIndexDescription);
    setMeta("name", "twitter:image", imageUrl);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = canonicalUrl;

    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: blogIndexTitle,
      description: blogIndexDescription,
      url: canonicalUrl,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: posts.map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: new URL(post.path, SITE_URL).toString(),
          name: post.title,
        })),
      },
    };
    let script = document.querySelector<HTMLScriptElement>('script[data-blog-index-schema]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.blogIndexSchema = "true";
      document.head.append(script);
    }
    script.text = JSON.stringify(schema);

    return () => script?.remove();
  }, [posts]);
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.append(element);
  }
  element.content = content;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value));
}
